import React, { useState, useRef } from 'react';
import {
  ChevronRight, Scale, Code2, Settings, Briefcase, TrendingUp, Server, Edit3,
  Upload, FileText, Loader2, CheckCircle2, X, AlertTriangle, Zap
} from 'lucide-react';
import api from '../../../api';
import toast from 'react-hot-toast';
import { ProjectSetup, ProjectType, MetricType } from '../datasim.types';

const PROJECT_TYPES: { id: ProjectType; label: string; icon: React.ElementType; description: string }[] = [
  { id: 'regulatory', label: 'Regulatory', icon: Scale, description: 'Compliance, audit, exam preparation' },
  { id: 'software', label: 'Software', icon: Code2, description: 'Build, migration, platform work' },
  { id: 'ops_transformation', label: 'Ops Transform', icon: Settings, description: 'Process redesign, change management' },
  { id: 'm_and_a', label: 'M&A Integration', icon: Briefcase, description: 'Merger integration, carve-out' },
  { id: 'marketing', label: 'Marketing', icon: TrendingUp, description: 'Campaign, launch, brand programs' },
  { id: 'infrastructure', label: 'Infrastructure', icon: Server, description: 'Data center, network, cloud' },
  { id: 'custom', label: 'Custom', icon: Edit3, description: 'Define your own project type' },
];

const METRIC_OPTIONS: { id: MetricType; label: string; units: string[] }[] = [
  { id: 'duration', label: 'Duration', units: ['weeks', 'months', 'days'] },
  { id: 'cost', label: 'Cost', units: ['$K', '$M', 'dollars'] },
  { id: 'effort', label: 'Effort', units: ['hours', 'days', 'story points'] },
  { id: 'custom', label: 'Custom', units: [] },
];

interface ExtractionResult {
  project_name?: string;
  project_type?: string;
  extracted_variables?: Array<{ name: string; type: string; notes?: string }>;
  detected_constraints?: string[];
  extraction_notes?: string;
}

interface Props {
  onComplete: (setup: ProjectSetup) => void;
}

export default function DataSimSetup({ onComplete }: Props) {
  const [projectName, setProjectName] = useState('');
  const [projectStartDate, setProjectStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [projectType, setProjectType] = useState<ProjectType>('software');
  const [customProjectType, setCustomProjectType] = useState('');
  const [metricType, setMetricType] = useState<MetricType>('duration');
  const [metricUnit, setMetricUnit] = useState('weeks');
  const [customMetricLabel, setCustomMetricLabel] = useState('');

  const [uploadedDocText, setUploadedDocText] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [extraction, setExtraction] = useState<ExtractionResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectedMetric = METRIC_OPTIONS.find(m => m.id === metricType)!;

  // Extensions whose raw bytes are valid UTF-8 text — for these we can use the
  // browser's `file.text()` and POST a JSON body (fastest path, no upload).
  // Anything else (Word, Excel, PowerPoint, PDF) is sent as multipart so the
  // backend's DocumentProcessor can do the format-specific extraction.
  const PLAIN_TEXT_EXTS = ['txt', 'md', 'csv'];

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadedFileName(file.name);
    setExtraction(null);
    setUploadLoading(true);

    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const isPlainText = PLAIN_TEXT_EXTS.includes(ext);

    try {
      let res;
      if (isPlainText) {
        // Fast path — read in browser, POST as JSON (original behaviour).
        const text = await file.text();
        setUploadedDocText(text);
        res = await api.post('/api/datasim/extract-document', { text, projectType });
      } else {
        // Binary formats — upload the file and let the backend extract text
        // via the same DocumentProcessor pipeline contract analysis uses.
        const formData = new FormData();
        formData.append('file', file);
        if (projectType) formData.append('projectType', projectType);
        res = await api.post('/api/datasim/extract-document', formData);
        // Backend echoes the extracted plain text — stash it so the manifest
        // / intake step downstream has the same context plain-text uploads
        // already provide.
        if (res?.data?.extracted_text) {
          setUploadedDocText(res.data.extracted_text);
        }
      }

      if (res?.data?.extraction) {
        const extResult: ExtractionResult = res.data.extraction;
        setExtraction(extResult);
        if (!projectName.trim() && extResult.project_name) setProjectName(extResult.project_name);
        if (extResult.project_type && PROJECT_TYPES.find(p => p.id === extResult.project_type)) {
          setProjectType(extResult.project_type as ProjectType);
        }
      }
    } catch (err: any) {
      // Surface the backend's specific error message when it gave one (e.g.
      // "file may be empty, image-only, or password-protected") so the user
      // knows whether to retry with a different file or proceed manually.
      const apiMsg = err?.response?.data?.error;
      toast.error(apiMsg || 'Document analysis failed — you can still proceed manually');
    } finally {
      setUploadLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  function clearDocument() {
    setUploadedDocText('');
    setUploadedFileName('');
    setExtraction(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function handleContinue() {
    if (!projectName.trim()) return;
    onComplete({
      projectName: projectName.trim(),
      projectType,
      customProjectType: projectType === 'custom' ? customProjectType : undefined,
      metricType,
      metricUnit: metricType === 'custom' ? (customMetricLabel || 'units') : metricUnit,
      metricLabel: metricType === 'custom' ? (customMetricLabel || 'Custom Metric') : selectedMetric.label,
      uploadedDocText: uploadedDocText || undefined,
      projectStartDate: projectStartDate || undefined,
    });
  }

  const canContinue = projectName.trim().length > 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

      {/* ── LEFT: Core setup (2 cols) ──────────────────────────────────── */}
      <div className="lg:col-span-2 space-y-4">

        {/* Project Name */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <label className="text-sm font-semibold text-gray-800 block mb-1">Project Name *</label>
          <p className="text-xs text-gray-500 mb-3">The engagement or initiative you're modeling.</p>
          <input
            type="text"
            autoFocus
            value={projectName}
            onChange={e => setProjectName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && canContinue && handleContinue()}
            placeholder="e.g. Heartland Bank AML Implementation, Q3 Platform Migration"
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200 transition-colors"
          />
        </div>

        {/* Project Start Date */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <label className="text-sm font-semibold text-gray-800 block mb-1">Project Start Date</label>
          <p className="text-xs text-gray-500 mb-3">Anchors the simulated durations to calendar dates so results show actual completion dates, not just week counts.</p>
          <input
            type="date"
            value={projectStartDate}
            onChange={e => setProjectStartDate(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200 transition-colors"
          />
        </div>

        {/* Project Type */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <label className="text-sm font-semibold text-gray-800 block mb-1">Project Type</label>
          <p className="text-xs text-gray-500 mb-3">Helps the AI analyst ask the right questions.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {PROJECT_TYPES.map(pt => {
              const Icon = pt.icon;
              const isSelected = projectType === pt.id;
              return (
                <button
                  key={pt.id}
                  onClick={() => setProjectType(pt.id)}
                  className={`flex flex-col items-start gap-1.5 px-3 py-2.5 rounded-lg border text-left transition-all ${
                    isSelected
                      ? 'bg-red-50 border-red-400 text-red-700'
                      : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-white'
                  }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="text-xs font-semibold">{pt.label}</span>
                </button>
              );
            })}
          </div>
          {projectType === 'custom' && (
            <input
              type="text"
              value={customProjectType}
              onChange={e => setCustomProjectType(e.target.value)}
              placeholder="Describe the project type..."
              className="mt-3 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400"
            />
          )}
        </div>

        {/* Primary Output Metric */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <label className="text-sm font-semibold text-gray-800 block mb-1">Confidence Interval Target</label>
          <p className="text-xs text-gray-500 mb-3">The primary output the Monte Carlo engine will simulate.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            {METRIC_OPTIONS.map(m => (
              <button
                key={m.id}
                onClick={() => { setMetricType(m.id); if (m.units.length > 0) setMetricUnit(m.units[0]); }}
                className={`px-3 py-2.5 rounded-lg border text-sm font-semibold transition-all ${
                  metricType === m.id
                    ? 'bg-red-50 border-red-400 text-red-700'
                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          {metricType !== 'custom' && selectedMetric.units.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">Unit</p>
              <div className="flex gap-2 flex-wrap">
                {selectedMetric.units.map(u => (
                  <button
                    key={u}
                    onClick={() => setMetricUnit(u)}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                      metricUnit === u
                        ? 'bg-gray-800 border-gray-800 text-white'
                        : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-400'
                    }`}
                  >
                    {u}
                  </button>
                ))}
              </div>
            </div>
          )}
          {metricType === 'custom' && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">Custom metric label</p>
              <input
                type="text"
                value={customMetricLabel}
                onChange={e => setCustomMetricLabel(e.target.value)}
                placeholder="e.g. Complexity Points, Story Points, FTE-weeks"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400"
              />
            </div>
          )}
        </div>

      </div>

      {/* ── RIGHT: Document upload + action (1 col) ────────────────────── */}
      <div className="space-y-4">

        {/* Document Upload */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <label className="text-sm font-semibold text-gray-800 block mb-1">
            Project Document <span className="text-gray-400 font-normal text-xs">(Optional)</span>
          </label>
          <p className="text-xs text-gray-500 mb-3">Upload a SOW or project plan — AI will extract variables automatically.</p>

          {!uploadedFileName ? (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 px-4 py-4 border-2 border-dashed border-gray-200 rounded-lg text-sm text-gray-400 hover:border-red-300 hover:text-red-500 hover:bg-red-50 transition-all"
            >
              <Upload className="w-4 h-4" />
              Click to upload (.pdf, .docx, .xlsx, .csv, .pptx, .txt, .md)
            </button>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <span className="text-xs text-gray-700 font-medium truncate">{uploadedFileName}</span>
                </div>
                <button onClick={clearDocument} className="p-1 rounded hover:bg-gray-200 text-gray-400 flex-shrink-0 ml-1">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>

              {uploadLoading && (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <Loader2 className="w-3.5 h-3.5 text-blue-500 animate-spin" />
                  <span className="text-xs text-blue-700">Analyzing with AI...</span>
                </div>
              )}

              {!uploadLoading && extraction && (
                <div className="border border-green-200 bg-green-50 rounded-lg p-3 space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                    <span className="text-xs font-semibold text-green-800">Analyzed</span>
                    {(extraction.extracted_variables?.length ?? 0) > 0 && (
                      <span className="text-xs bg-green-200 text-green-800 px-1.5 py-0.5 rounded-full font-medium">
                        {extraction.extracted_variables!.length} vars
                      </span>
                    )}
                  </div>
                  {extraction.extraction_notes && (
                    <p className="text-xs text-green-700 leading-snug">{extraction.extraction_notes}</p>
                  )}
                  {(extraction.extracted_variables?.length ?? 0) > 0 && (
                    <div className="space-y-0.5">
                      {extraction.extracted_variables!.slice(0, 4).map((v, i) => (
                        <div key={i} className="flex items-center gap-1.5">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                          <span className="text-xs text-green-800 truncate">{v.name}</span>
                        </div>
                      ))}
                      {extraction.extracted_variables!.length > 4 && (
                        <p className="text-xs text-green-600 ml-3">+{extraction.extracted_variables!.length - 4} more</p>
                      )}
                    </div>
                  )}
                  {(extraction.detected_constraints?.length ?? 0) > 0 && (
                    <div className="flex items-start gap-1">
                      <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-amber-700">
                        {extraction.detected_constraints!.map(c => c.replace(/_/g, ' ')).join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".txt,.md,.csv,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,text/plain,text/markdown,text/csv,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        {/* What to expect */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm space-y-2">
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">What happens next</p>
          {[
            { step: '1', text: 'AI analyst interviews you about project variables' },
            { step: '2', text: 'Configure risk constraints & external dependencies' },
            { step: '3', text: 'Optionally model AI impact on timelines' },
            { step: '4', text: 'Run 10,000-iteration Monte Carlo simulation' },
          ].map(s => (
            <div key={s.step} className="flex items-start gap-2">
              <span className="flex-shrink-0 w-4 h-4 rounded-full bg-red-100 text-red-600 text-[10px] font-bold flex items-center justify-center mt-0.5">
                {s.step}
              </span>
              <span className="text-xs text-gray-500 leading-snug">{s.text}</span>
            </div>
          ))}
        </div>

        {/* Begin button */}
        <button
          onClick={handleContinue}
          disabled={!canContinue || uploadLoading}
          className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-red-600 text-white rounded-xl font-semibold text-sm hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
        >
          {uploadLoading ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing document...</>
          ) : (
            <><Zap className="w-4 h-4" /> Begin AI Intake Interview <ChevronRight className="w-4 h-4" /></>
          )}
        </button>

      </div>
    </div>
  );
}
